import React, { useContext, useState } from 'react';
import {
    Col,
    Row,
    Container,
    Nav,
    NavItem,
    NavLink,
    Button,
    Alert,
} from 'reactstrap'
import AccountPasswordResetForm from '../../components/Account/AccountPasswordReset';
import AccountPasswordChangeForm from '../../components/Account/AccountPasswordChange';
import { AuthUserContext, withAuthorization, withOrganization } from '../../components/Session';
import { FirebaseContext } from '../../components/Firebase';
import { useOrganizationStore } from '../../store/organization.store';
import { useNotificationsStore } from '../../store/notifications.store';
import {
    NOTIFICATION_TYPES_ORDER,
    NOTIFICATION_LABELS,
    defaultPreferences,
    NotificationPreferences,
} from '../../services/notificationPreferences';
import type { NotificationType } from '../../services/notification.service';

/**
 * Toggle switch (sliding pill) — pixel-identical to the one used in Music Vault's
 * Account page so the two apps stay visually aligned.
 */
const ToggleSwitch: React.FC<{ checked: boolean; onChange: () => void; ariaLabel?: string }> = ({ checked, onChange, ariaLabel }) => (
    <button
        type="button"
        onClick={onChange}
        aria-label={ariaLabel}
        aria-pressed={checked}
        style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '36px',
            minWidth: '36px',
            height: '20px',
            borderRadius: '10px',
            border: 'none',
            backgroundColor: checked ? 'var(--accent-1-color)' : 'rgba(255, 255, 255, 0.2)',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            position: 'relative',
            padding: 0,
        }}
    >
        <div
            style={{
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                backgroundColor: '#fff',
                position: 'absolute',
                left: checked ? '18px' : '2px',
                transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }}
        />
    </button>
);

const AccountPage = () => {
    const firebase = useContext(FirebaseContext);
    const currentOrgId = useOrganizationStore(state => state.currentOrgId);
    const currentOrgName = useOrganizationStore(state => state.currentOrgName);
    const setInAppPrefs = useNotificationsStore(state => state.setInAppPrefs);

    const [activeTab, setActiveTab] = useState('overview');

    // Notification preferences state — loaded lazily on tab open, scoped to the
    // current org's member document (preferences are per-membership).
    const [notifPrefs, setNotifPrefs] = useState<NotificationPreferences>(defaultPreferences());
    const [notifLoaded, setNotifLoaded] = useState(false);
    const [notifSaving, setNotifSaving] = useState(false);
    const [notifSaveSuccess, setNotifSaveSuccess] = useState(false);
    // Snapshot of what was loaded from Firestore so we can detect
    // per-type OFF→ON transitions on save and stamp the "enabled since"
    // timestamp accordingly.
    const [notifPrefsSnapshot, setNotifPrefsSnapshot] = useState<NotificationPreferences>(defaultPreferences());
    const setInAppEnabledSince = useNotificationsStore(state => state.setInAppEnabledSince);

    const loadNotifPrefs = async (userId: string) => {
        if (notifLoaded || !currentOrgId || !firebase) return;
        try {
            const prefs = await firebase.getMemberNotificationPreferences(currentOrgId, userId);
            setNotifPrefs(prefs);
            setNotifPrefsSnapshot(prefs);
            setNotifLoaded(true);
        } catch (err) {
            console.error('Error loading notification preferences:', err);
            setNotifLoaded(true);
        }
    };

    const saveNotifPrefs = async (userId: string) => {
        if (!currentOrgId || !firebase) return;
        setNotifSaving(true);
        setNotifSaveSuccess(false);
        try {
            // For every type that just flipped from OFF (false) → ON
            // (true), stamp inAppEnabledSince[type] = now so subscribers
            // filter out the accumulated backlog created while muted.
            // Types that flipped ON→OFF or stayed the same leave the
            // timestamp untouched.
            const now = Date.now();
            const nextInAppEnabledSince = { ...notifPrefs.inAppEnabledSince };
            for (const type of NOTIFICATION_TYPES_ORDER) {
                const was = notifPrefsSnapshot.inApp[type];
                const isNow = notifPrefs.inApp[type];
                if (!was && isNow) {
                    nextInAppEnabledSince[type] = now;
                }
            }
            const payload: NotificationPreferences = {
                ...notifPrefs,
                inAppEnabledSince: nextInAppEnabledSince,
            };
            await firebase.updateMemberNotificationPreferences(currentOrgId, userId, payload);
            // Reflect the change in the bell badge immediately — otherwise the
            // count wouldn't update until the next org switch / page reload.
            setInAppPrefs(payload.inApp);
            setInAppEnabledSince(payload.inAppEnabledSince);
            setNotifPrefs(payload);
            setNotifPrefsSnapshot(payload);
            setNotifSaveSuccess(true);
            setTimeout(() => setNotifSaveSuccess(false), 3000);
        } catch (err) {
            console.error('Error saving notification preferences:', err);
        } finally {
            setNotifSaving(false);
        }
    };

    const toggleNotifPref = (channel: 'inApp' | 'email', type: NotificationType) => {
        setNotifPrefs(prev => ({
            ...prev,
            [channel]: { ...prev[channel], [type]: !prev[channel][type] },
        }));
    };

    const toggle = (tab: string) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    const renderTabContent = (authUser: any) => {
        switch (activeTab) {
            case 'overview':
                return (
                    <div>
                        <h4 className="mb-4">Account Overview</h4>
                        <div className="mb-3">
                            <p className="mb-2"><strong>User Name:</strong> {authUser.username}</p>
                            <p className="mb-2"><strong>Email:</strong> {authUser.email}</p>
                        </div>
                        <p className="text-muted">Manage your account settings and preferences.</p>
                    </div>
                );
            case 'password':
                return (
                    <div>
                        <h4 className="mb-4">Password Management</h4>
                        <Row>
                            <Col md="6">
                                <div className="mb-4">
                                    <AccountPasswordChangeForm />
                                </div>
                                <div>
                                    <AccountPasswordResetForm />
                                </div>
                            </Col>
                            <Col md="6">
                                {/* Space for additional fields on the right */}
                            </Col>
                        </Row>
                    </div>
                );
            case 'notifications':
                if (!notifLoaded && authUser?.uid) {
                    loadNotifPrefs(authUser.uid);
                }
                return (
                    <div>
                        <h4 className="mb-4">Notification Preferences</h4>
                        {currentOrgName && (
                            <p className="text-muted mb-4">
                                Manage which notifications you receive for <strong>{currentOrgName}</strong>.
                            </p>
                        )}
                        <Row>
                            <Col md="8">
                                <div className="mb-4">
                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 90px 90px',
                                            gap: '0.5rem 1rem',
                                            alignItems: 'center',
                                            paddingBottom: '0.5rem',
                                            borderBottom: '1px solid rgba(255,255,255,0.08)',
                                            marginBottom: '0.5rem',
                                        }}
                                    >
                                        <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            Notification
                                        </span>
                                        <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>
                                            In-app
                                        </span>
                                        <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>
                                            Email
                                        </span>
                                    </div>
                                    {NOTIFICATION_TYPES_ORDER.map((type) => (
                                        <div
                                            key={type}
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: '1fr 90px 90px',
                                                gap: '0.5rem 1rem',
                                                alignItems: 'center',
                                                padding: '0.75rem 0',
                                                borderBottom: '1px solid rgba(255,255,255,0.04)',
                                            }}
                                        >
                                            <span style={{ color: '#fff', fontSize: '0.9rem' }}>
                                                {NOTIFICATION_LABELS[type]}
                                            </span>
                                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                <ToggleSwitch
                                                    checked={notifPrefs.inApp[type]}
                                                    onChange={() => toggleNotifPref('inApp', type)}
                                                    ariaLabel={`In-app ${NOTIFICATION_LABELS[type]}`}
                                                />
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                <ToggleSwitch
                                                    checked={notifPrefs.email[type]}
                                                    onChange={() => toggleNotifPref('email', type)}
                                                    ariaLabel={`Email ${NOTIFICATION_LABELS[type]}`}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                    <Button
                                        className="btn-info-custom mt-3"
                                        onClick={() => saveNotifPrefs(authUser.uid)}
                                        disabled={notifSaving}
                                    >
                                        <span style={{ position: 'relative', zIndex: 2 }}>
                                            {notifSaving ? 'Saving...' : 'Save Preferences'}
                                        </span>
                                    </Button>
                                    {notifSaveSuccess && (
                                        <Alert color="success" className="mt-3 mb-0">
                                            Notification preferences saved!
                                        </Alert>
                                    )}
                                </div>
                            </Col>
                            <Col md="4">
                                <div className="p-3" style={{ backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                                    <h6 className="mb-2">About Notifications</h6>
                                    <p className="text-muted small mb-2">
                                        These preferences control which notifications you receive within the app and via email.
                                        They apply to your membership in the current organization.
                                    </p>
                                    <p className="text-muted small mb-0">
                                        Switching organizations may show different notification preferences
                                        based on your role in each organization.
                                    </p>
                                </div>
                            </Col>
                        </Row>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <AuthUserContext.Consumer>
            {(authUser: any) => (
                <div className="wrapper pt-3 pb-3">
                    <Container fluid style={{paddingLeft: '100px', paddingRight: '100px'}}>
                        <Row>
                            <Col md="2" lg="2" className="pr-3">
                                <div className="mb-4">
                                    <h4 className="d-flex flex-row align-items-center mb-2">
                                        <i className="tim-icons icon-single-02 accent-1-icon mr-2"></i> 
                                        {authUser.username}
                                    </h4>
                                </div>
                                
                                <Nav vertical className="nav-pills">
                                    <NavItem>
                                        <NavLink
                                            className={activeTab === 'overview' ? 'active' : ''}
                                            onClick={() => toggle('overview')}
                                        >
                                            <i className="tim-icons icon-chart-pie-36 mr-2"></i>
                                            Overview
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={activeTab === 'password' ? 'active' : ''}
                                            onClick={() => toggle('password')}
                                        >
                                            <i className="tim-icons icon-lock-circle mr-2"></i>
                                            Password
                                        </NavLink>
                                    </NavItem>
                                    <NavItem>
                                        <NavLink
                                            className={activeTab === 'notifications' ? 'active' : ''}
                                            onClick={() => toggle('notifications')}
                                        >
                                            <i className="tim-icons icon-bell-55 mr-2"></i>
                                            Notifications
                                        </NavLink>
                                    </NavItem>
                                </Nav>
                            </Col>
                            
                            <Col md="10" lg="10">
                                <div className="card">
                                    <div className="card-body">
                                        {renderTabContent(authUser)}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Container>
                </div>
            )}
        </AuthUserContext.Consumer>
    );
};

const condition = (authUser: any) => !!authUser;
export default withOrganization(withAuthorization(condition)(AccountPage));